import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { NC_COUNTIES } from "@/lib/ncCounties";
import { MapPin } from "lucide-react";
import { AllCities } from "@/components/PlaceLinks";
import { usePlaceLang, altLinks } from "@/lib/placeLang";
import { pageText } from "@/lib/i18n/pages";
import { trackView } from "@/lib/trackView";

const SITE = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  en: {
    title: "North Carolina Court Forms by County — All 100 NC Counties | NC Legal Forms",
    desc: "Find official North Carolina court forms for all 100 counties — from Alamance to Yancey. Fill, autofill, print and download AOC forms for divorce, custody, child support, small claims and more, county by county.",
    over: "Statewide · All 100 counties", h1: "North Carolina Court Forms by County",
    intro: "North Carolina court forms are standardized statewide, but every case is filed in a specific county. Choose your county below for the forms, courthouse and filing details relevant to you — or use our AI Form Finder to jump straight to the right document.",
    county: (c) => `${c} County`,
  },
  es: {
    title: "Formularios judiciales de Carolina del Norte por condado — Los 100 condados | NC Legal Forms",
    desc: "Encuentre formularios judiciales oficiales de Carolina del Norte para los 100 condados. Llene, imprima y descargue formularios AOC de divorcio, custodia, manutención, reclamos menores y más.",
    over: "Todo el estado · Los 100 condados", h1: "Formularios judiciales de Carolina del Norte por condado",
    intro: "Los formularios judiciales de Carolina del Norte son iguales en todo el estado, pero cada caso se presenta en un condado específico. Elija su condado para ver los formularios, el tribunal y los detalles de presentación — o use nuestro buscador con IA para ir directo al documento correcto.",
    county: (c) => `Condado de ${c}`,
  },
};

export default function Counties() {
  const { lang, base, ready } = usePlaceLang();
  useEffect(() => { if (ready) trackView({ kind: "index", lang }); }, [ready, lang]);
  const L = pageText(lang, "counties") || (lang === "es" ? T.es : T.en);
  const url = `${SITE}${base}`;

  return (
    <div>
      <Helmet>
        <html lang={lang} />
        <title>{L.title}</title>
        <meta name="description" content={L.desc} />
        <link rel="canonical" href={url} />
        {altLinks(SITE, "").map((l) => <link key={l.hrefLang} rel="alternate" hrefLang={l.hrefLang} href={l.href} />)}
        <meta property="og:title" content={L.title} />
        <meta property="og:description" content={L.desc} />
        <meta property="og:url" content={url} />
      </Helmet>
      <Navbar />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-8" data-testid="counties-page">
        <p className="overline text-accent mb-3 flex items-center gap-2"><MapPin size={14} /> {L.over}</p>
        <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6" data-testid="counties-title">{L.h1}</h1>
        <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">{L.intro}</p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {NC_COUNTIES.map((c) => (
            <Link key={c.slug} to={`${base}/${c.slug}`} data-testid={`county-link-${c.slug}`}
              className="border-2 border-border px-4 py-3 flex items-center justify-between group hover:bg-accent hover:text-accent-foreground transition-colors">
              <span className="font-medium">{L.county(c.name)}</span>
              <span className="text-xs opacity-60 group-hover:opacity-100">{c.seat}</span>
            </Link>
          ))}
        </div>
      </section>

      <AllCities lang={lang} />

      <Footer />
    </div>
  );
}
